/**
 * "Acknowledge & accepted" in Geosoft QC (MB's R58, decision 538; spec docs/specs/geosoft-qc.md §9).
 *
 * A line that failed the QC or needs a check can be reviewed in the field and accepted without a refly. Managers,
 * data processors and field crew may do it, with a reason. The line's QC result keeps its verdict (Fail or Check);
 * the acknowledgement is a `qc_acceptance` row of its own (who, when, why, and what the line's review was before),
 * and the line's Line review row becomes accepted, so the line counts as accepted km in the progress figures and
 * reports and the client's reports show it as accepted. The Line review history keeps the change with the QC result
 * in its reason. "Undo" (with a reason) is another row: the line's review goes back to what it was.
 *
 * Teslyn Field writes both in one transaction (`qc_acknowledge`, `qc_acknowledge_undo` in teslyn-db); Teslyn Cloud
 * stamps who and refuses other roles (20261025001000_qc_acceptance.sql).
 *
 * A test line (801/802, numbered under 10000: decision 344) has no Line review row. A flagged one is acknowledged and
 * accepted all the same (decision 861, MB's Q766): the `qc_acceptance` row has who, when and why and no line status;
 * nothing is made or changed in Line review, and no refly is planned.
 */
import type { Acceptance, Common, IsoDateTime, LineStatus, Uuid } from './model.js';
import type { QcLineResult, QcRun } from './geosoft-qc.js';
import { MAX_REASON_LENGTH } from './acceptance.js';
import { parseLineNumber } from './lines.js';

/** The state's words, wherever it is shown. */
export const QC_ACCEPTED_WORDS = 'Acknowledged & accepted';

/** Decision 538, 1 A. Teslyn Cloud's policy on `qc_acceptance` holds the same list. */
export const QC_ACKNOWLEDGE_ROLES = ['manager', 'data_processor', 'field_crew'] as const;

/** The longest reason kept with the acknowledgement (the Line review reason is shortened to 100 characters). */
export const QC_ACK_REASON_MAX = 500;

/** `qc_acceptance` (crates/teslyn-db 0041, supabase 20261025001000): one acknowledgement, or its undo. */
export interface QcAcceptance extends Common {
  readonly project_id: Uuid;
  readonly qc_line_result_id: Uuid;
  readonly line_status_id: Uuid | null;
  readonly action: 'accepted' | 'undone';
  /** The QC verdict acknowledged, kept as it was. */
  readonly verdict: 'fail' | 'warn';
  readonly reason: string;
  /** Who: the signed-in person's name (Teslyn Cloud sets it from the account). */
  readonly by_name: string;
  readonly acted_at: IsoDateTime;
  /** The line's review before the acknowledgement, restored by an undo. */
  readonly from_acceptance: Acceptance | null;
  readonly from_accepted_m: number | null;
  readonly from_reason: string;
}

/** Whether these roles may acknowledge and accept. `null` (roles not known on this laptop yet): offered; the cloud decides. */
export function canAcknowledgeQc(roles: readonly string[] | null): boolean {
  if (roles === null) return true;
  return roles.some((r) => (QC_ACKNOWLEDGE_ROLES as readonly string[]).includes(r));
}

/** A test line (801 pre-block, 802 post-block; decision 344): never in Line review, acknowledged without a row. */
export function isQcTestLine(line: Pick<QcLineResult, 'line_number'>): boolean {
  return (
    line.line_number !== null &&
    Number.isInteger(line.line_number) &&
    line.line_number >= 0 &&
    parseLineNumber(line.line_number).kind === 'test'
  );
}

const KIND_WORDS = {
  comp_box: ['comp box line', 'Comp box lines'],
  ground_check: ['ground check', 'Ground checks'],
};

/**
 * Why a QC line has no Line review row, for the note under its plots (issue #71): a test line is never in Line review
 * (but can be acknowledged); a comp box line or ground check never is; a survey line's run may have no flight, its
 * flight's log may not be imported, the line may have come in after the QC ran (run it again to link it), or the log
 * has no such line. `statuses`: the project's Line review rows.
 */
export function qcNoReviewRowWords(
  line: Pick<QcLineResult, 'line_name' | 'line_number'>,
  run: Pick<QcRun, 'flight_id'>,
  statuses: readonly Pick<LineStatus, 'flight_id' | 'line_number' | 'deleted_at'>[],
): string {
  const n = line.line_number;
  const name = line.line_name;
  if (n === null || !Number.isInteger(n) || n < 0)
    return `${name} has no line number Teslyn knows, so it is not in Line review and cannot be reviewed here.`;
  const parsed = parseLineNumber(n);
  if (!parsed.production) {
    const short = Math.trunc(n / 10);
    if (parsed.kind === 'test')
      return `${name} is test line ${short}. Test lines are not in Line review, so there is nothing to accept, part-accept or reject here; a test line that failed or needs a check can be acknowledged and accepted in its row.`;
    if (parsed.kind === 'comp_box' || parsed.kind === 'ground_check') {
      const [one, many] = KIND_WORDS[parsed.kind];
      return `${name} is ${one} ${short}. ${many} are not in Line review, so it cannot be reviewed here.`;
    }
    return `${name} is not a survey line (lines numbered under 10000 are calibration or test lines), so it is not in Line review and cannot be reviewed here.`;
  }
  if (run.flight_id === null)
    return `${name} is not in Line review: this QC run is not linked to a flight in Flight logs, so it cannot be reviewed here.`;
  const live = statuses.filter((s) => s.deleted_at === null);
  if (!live.some((s) => s.flight_id === run.flight_id))
    return `${name} is not in Line review yet: its flight's log is not imported in Flight logs (on its daily entry, or on Daily entry's "All flight logs" tab), so it cannot be reviewed here.`;
  // the run links a line to the flight's own row of its number, else the newest of that number (teslyn-db qc_run_save)
  if (live.some((s) => s.line_number === n))
    return `${name} is not in Line review for this run: line ${n} was imported in Flight logs after the QC ran. Run the QC of the flight again to link it.`;
  return `${name} is not in Line review: its flight's log in Flight logs has no line ${n}, so it cannot be reviewed here.`;
}

/** Why a line cannot be acknowledged and accepted now; empty when it can. */
export function qcAcknowledgeBlockers(
  line: QcLineResult,
  roles: readonly string[] | null,
  reason: string,
): string[] {
  if (line.verdict !== 'fail' && line.verdict !== 'warn')
    return ['Only a line that failed or needs a check can be acknowledged and accepted.'];
  if (!canAcknowledgeQc(roles))
    return ['Only managers, data processors and field crew can acknowledge and accept a line.'];
  // decision 861: a test line has no Line review row and needs none
  if (line.line_status_id === null && !isQcTestLine(line))
    return [`${line.line_name} is not in Line review yet: import the flight's log first.`];
  const r = reason.trim();
  if (r === '') return ['A reason is required to acknowledge and accept a line.'];
  if (r.length > QC_ACK_REASON_MAX)
    return [`The reason is ${r.length} characters; keep it to ${QC_ACK_REASON_MAX}.`];
  return [];
}

/**
 * MB's R212: why a line of a bulk "Acknowledge & accept" is left out, in the words the result names it with
 * ("L301780 (already acknowledged and accepted)"); null when the line takes it. teslyn-db `qc_acknowledge_many`
 * guards the same rules in its transaction. `status` is the line's Line review row: null when it is not there any
 * more, undefined when it was not read here (the transaction checks it).
 */
export function qcBulkAcknowledgeSkip(
  line: Pick<QcLineResult, 'id' | 'verdict' | 'line_status_id' | 'line_number'>,
  acceptances: readonly QcAcceptance[],
  status?: Pick<LineStatus, 'acceptance' | 'deleted_at'> | null,
): string | null {
  if (line.verdict !== 'fail' && line.verdict !== 'warn') return 'it passed the QC';
  if (qcAcceptanceState(acceptances, line.id).accepted) return 'already acknowledged and accepted';
  if (line.line_status_id === null) return isQcTestLine(line) ? null : 'not in Line review yet';
  if (status === null) return 'not in Line review';
  if (status === undefined) return null;
  if (status.deleted_at !== null) return 'deleted from Line review';
  if (status.acceptance === 'accepted') return 'already accepted in Line review';
  return null;
}

/** MB's R212: why a bulk "Acknowledge & accept" cannot go ahead; empty when it can. */
export function qcBulkAcknowledgeBlockers(
  roles: readonly string[] | null,
  reason: string,
  count: number,
): string[] {
  if (!canAcknowledgeQc(roles))
    return ['Only managers, data processors and field crew can acknowledge and accept a line.'];
  if (count === 0) return ['Choose the lines to acknowledge and accept.'];
  const r = reason.trim();
  if (r === '') return ['A reason is required to acknowledge and accept the lines.'];
  if (r.length > QC_ACK_REASON_MAX)
    return [`The reason is ${r.length} characters; keep it to ${QC_ACK_REASON_MAX}.`];
  return [];
}

const linesWord = (n: number): string => `${n} ${n === 1 ? 'line' : 'lines'}`;

/**
 * MB's R212: what a bulk "Acknowledge & accept" did, in words: the lines acknowledged and accepted, what they count,
 * and each line left out with why.
 */
export function qcBulkAcknowledgeWords(
  done: readonly Pick<QcLineResult, 'line_name' | 'line_number'>[],
  skipped: readonly { readonly line_name: string; readonly why: string }[],
): string {
  const parts: string[] = [];
  if (done.length > 0) {
    const tests = done.filter((l) => isQcTestLine(l)).length;
    const counts =
      tests === 0
        ? 'They count as accepted km.'
        : tests === done.length
          ? 'Test lines: nothing changes in Line review and they count no km.'
          : 'The survey lines count as accepted km; the test lines count no km.';
    parts.push(
      `${linesWord(done.length)} ${QC_ACCEPTED_WORDS.toLowerCase()}: ${done.map((l) => l.line_name).join(', ')}. ${counts}`,
    );
  } else parts.push(`No lines ${QC_ACCEPTED_WORDS.toLowerCase()}.`);
  if (skipped.length > 0)
    parts.push(
      `${linesWord(skipped.length)} left out: ${skipped.map((x) => `${x.line_name} (${x.why})`).join(', ')}.`,
    );
  return parts.join(' ');
}

/** A label lower-cased at its start, except a channel name (`MagTF1U_ED against VecTF` stays as it is). */
const firstLower = (s: string): string => {
  const word = s.split(' ')[0] ?? '';
  return s === '' || /[_\d]/.test(word) ? s : s[0]!.toLowerCase() + s.slice(1);
};
const fit = (text: string): string =>
  text.length <= MAX_REASON_LENGTH ? text : `${text.slice(0, MAX_REASON_LENGTH - 1)}…`;

/** What the QC found on the line, short: `terrain clearance 1 ×; below lower working height 2 ×`. */
export function qcFoundWords(line: Pick<QcLineResult, 'tests' | 'verdict'>): string {
  const tests = line.tests.filter((t) => t.status === line.verdict);
  return tests.map((t) => `${firstLower(t.label)} ${t.events.length} ×`).join('; ');
}

/** What the QC found, short: `fail: terrain clearance 1 ×` or `check: …`. */
function found(line: QcLineResult): string {
  return `${line.verdict === 'fail' ? 'fail' : 'check'}: ${qcFoundWords(line)}`;
}

/** The Line review reason of an acknowledged line (at most 100 characters). */
export function qcAcknowledgeLineReason(line: QcLineResult, reason: string): string {
  return fit(`${QC_ACCEPTED_WORDS} (QC ${found(line)}): ${reason.trim()}`);
}

/** The Line review history reason of an undo (the line's own reason goes back to what it was). */
export function qcUndoLineReason(reason: string): string {
  return fit(`${QC_ACCEPTED_WORDS} undone: ${reason.trim()}`);
}

const live = (rows: readonly QcAcceptance[], resultId: Uuid): QcAcceptance[] =>
  rows
    .filter((r) => r.qc_line_result_id === resultId && r.deleted_at === null)
    .sort((a, b) => a.acted_at.localeCompare(b.acted_at) || a.created_at.localeCompare(b.created_at));

/** The line result's state: acknowledged and accepted when its newest row is an acknowledgement. */
export function qcAcceptanceState(
  rows: readonly QcAcceptance[],
  resultId: Uuid,
): { readonly accepted: boolean; readonly current: QcAcceptance | null } {
  const own = live(rows, resultId);
  const current = own[own.length - 1] ?? null;
  return { accepted: current?.action === 'accepted', current };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `25 Oct 2026 11:00` at a UTC offset in minutes (the computer's by default). */
export function qcTimeWords(at: IsoDateTime, offsetMin: number = -new Date().getTimezoneOffset()): string {
  return when(at, offsetMin);
}

function when(at: IsoDateTime, offsetMin: number): string {
  const d = new Date(Date.parse(at) + offsetMin * 60_000);
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()} ${hh}:${mm}`;
}

/** Every acknowledgement and undo of the line result, oldest first, as sentences for the QC record. */
export function qcAcceptanceHistory(
  rows: readonly QcAcceptance[],
  resultId: Uuid,
  offsetMin: number = -new Date().getTimezoneOffset(),
): { readonly row: QcAcceptance; readonly text: string }[] {
  return live(rows, resultId).map((row) => {
    const who = row.by_name.trim() || 'someone';
    const at = when(row.acted_at, offsetMin);
    const text =
      row.action === 'accepted'
        ? `${QC_ACCEPTED_WORDS} by ${who}, ${at} (was ${row.verdict === 'fail' ? 'Fail' : 'Check'}): ${row.reason}`
        : `Undone by ${who}, ${at}: ${row.reason}`;
    return { row, text };
  });
}
